import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { NetworkStatus } from '@cbridge-utils/network';
import { makeClientSymbolCountersCache, makeClientSymbolCountersCacheId } from './_helpers/clientSymbolCountersCache';
import { makeClientSymbolCompositeId } from './_helpers/makeClientSymbolCompositeId';
import { SymbolClientAction } from './symbolClient.action';
import { symbolClientInitialState } from './symbolClient.constant';
import { ISymbolClientStore } from './symbolClient.interface';

export const symbolClientReducer = reducerWithInitialState(symbolClientInitialState)
  .case(SymbolClientAction.load.started, (state, params): ISymbolClientStore => {
    const { data } = state;
    const clientSymbolId = makeClientSymbolCompositeId(params);

    return {
      ...state,
      data: {
        ...data,
        [clientSymbolId]: {
          ...data[clientSymbolId],
          networkStatus: NetworkStatus.Started,
        },
      },
    };
  })
  .case(SymbolClientAction.load.done, (state, payload): ISymbolClientStore => {
    const { data } = state;
    const { params, result } = payload;
    const clientSymbolId = makeClientSymbolCompositeId(params);

    return {
      ...state,
      data: {
        ...data,
        [clientSymbolId]: {
          ...result,
          networkStatus: NetworkStatus.Done,
        },
      },
    };
  })
  .case(SymbolClientAction.load.failed, (state, { params }): ISymbolClientStore => {
    const { data } = state;
    const clientSymbolId = makeClientSymbolCompositeId(params);

    return {
      ...state,
      data: {
        ...data,
        [clientSymbolId]: {
          ...data[clientSymbolId],
          networkStatus: NetworkStatus.Failed,
        },
      },
    };
  })
  .cases([
    SymbolClientAction.loadListFromNode.started,
    SymbolClientAction.loadListFromServer.started,
    SymbolClientAction.processing.started,
  ], (state) => {
    return {
      ...state,
      listNetworkStatus: NetworkStatus.Started,
      listError: undefined,
    };
  })
  .cases([SymbolClientAction.loadListFromNode.done, SymbolClientAction.loadListFromServer.done], (state, payload): ISymbolClientStore => {
    const data = { ...state.data };
    const { params, result } = payload;
    const [key, counters] = makeClientSymbolCountersCache(params, result);

    for (const item of result.items) {
      data[item.coreSymbol.symbolId] = {
        ...data[item.coreSymbol.symbolId],
        ...item,
      };
    }

    return {
      ...state,
      data,
      counters: {
        ...state.counters,
        [key]: counters,
      },
      listNetworkStatus: NetworkStatus.Done,
    };
  })
  .cases([SymbolClientAction.loadListFromNode.failed, SymbolClientAction.loadListFromServer.failed], (state, { error }) => {
    return {
      ...state,
      listNetworkStatus: NetworkStatus.Failed,
      listError: error,
    };
  })
  .case(SymbolClientAction.loadCounters.done, (state, { params, result }): ISymbolClientStore => {
    return {
      ...state,
      counters: {
        ...state.counters,
        [makeClientSymbolCountersCacheId(params)]: result,
      },
    };
  })
  .case(SymbolClientAction.update.done, (state, payload) => {
    const data = { ...state.data };
    const { params } = payload;
    const { clientSymbolName, name, nodeId, serverId } = params;
    const clientSymbolIdOld = makeClientSymbolCompositeId(
      serverId
        ? { serverId, name: clientSymbolName }
        : { nodeId, name: clientSymbolName },
    );
    const clientSymbolId = makeClientSymbolCompositeId(
      serverId
        ? { serverId, name }
        : { nodeId, name },
    );

    data[clientSymbolId] = {
      ...data[clientSymbolIdOld],
      clientSymbolName: name,
    };

    return {
      ...state,
      data,
    };
  })
  .cases([SymbolClientAction.processing.done, SymbolClientAction.processing.failed], (state) => {
    return {
      ...state,
      listNetworkStatus: NetworkStatus.Done,
    };
  })
  .case(SymbolClientAction.destroy, () => ({
    ...symbolClientInitialState,
  }));
